import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { Gauge, ChartLineUp, Notebook, Shield, Brain, Pulse, Calendar, Gear, SignOut, Lightning, Sparkle, Crown, List, X, Calculator,
} from "@phosphor-icons/react";
import OnboardingGuide, { shouldShowOnboarding } from "@/components/OnboardingGuide";

const PAGE_TITLES = {
  "/app": "Dashboard", "/app/analyze": "AI Analýza grafu", "/app/journal": "Obchodní deník",
  "/app/propfirm": "Prop Firm Guardian", "/app/mentor": "AI Mentor", "/app/scanner": "Market Scanner",
  "/app/calendar": "Ekonomický kalendář", "/app/calculator": "Risk kalkulačka",
  "/app/settings": "Nastavení", "/app/upgrade": "Předplatné", "/app/admin": "Admin",
};

const NAV_ITEMS = [
  { to: "/app", key: "sidebar.dashboard", icon: Gauge, end: true, testid: "dashboard" },
  { to: "/app/analyze", key: "sidebar.analyze", icon: ChartLineUp, testid: "analyze" },
  { to: "/app/journal", key: "sidebar.journal", icon: Notebook, testid: "journal" },
  { to: "/app/propfirm", key: "sidebar.propfirm", icon: Shield, testid: "propfirm" },
  { to: "/app/mentor", key: "sidebar.mentor", icon: Brain, testid: "mentor" },
  { to: "/app/scanner", key: "sidebar.scanner", icon: Pulse, testid: "scanner" },
  { to: "/app/calendar", key: "sidebar.calendar", icon: Calendar, testid: "calendar" },
  { to: "/app/calculator", key: "sidebar.calculator", icon: Calculator, testid: "calculator" },
  { to: "/app/settings", key: "sidebar.settings", icon: Gear, testid: "settings" },
];

const Logo = ({ size = "md" }) => (
  <NavLink to="/" className="flex items-center gap-2">
    <img src="/icons/icon-192.png" alt="TradeMind AI" className={`${size === "sm" ? "w-7 h-7" : "w-8 h-8"} rounded-md`} />
    <span className="font-semibold tracking-tight text-zinc-50">TradeMind <span className="text-blue-500">AI</span></span>
  </NavLink>
);

const NavContent = ({ idPrefix = "side", onNavigate }) => {
  const { t, lang, setLang } = useT();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`${idPrefix}-${item.testid}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${isActive ? "bg-zinc-900 text-zinc-50 border border-zinc-800" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 border border-transparent"}`}
          >
            <item.icon weight="bold" size={18} />
            <span>{t(item.key)}</span>
          </NavLink>
        ))}
        {user?.plan === "FREE" && (
          <NavLink to="/app/upgrade" onClick={onNavigate} data-testid={`${idPrefix}-upgrade`} className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm bg-gradient-to-r from-blue-600/15 to-blue-600/5 border border-blue-500/30 text-blue-300 hover:bg-blue-600/20 mt-3 transition-colors">
            <Sparkle weight="fill" size={18} />
            <span className="font-medium">{lang === "cs" ? "Upgrade na PREMIUM" : "Upgrade to PREMIUM"}</span>
          </NavLink>
        )}
        {user?.is_admin && (
          <NavLink to="/app/admin" onClick={onNavigate} data-testid={`${idPrefix}-admin`} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors mt-3 border ${isActive ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "bg-amber-500/5 border-amber-500/20 text-amber-400 hover:bg-amber-500/10"}`}>
            <Crown weight="fill" size={18} />
            <span className="font-medium">Admin Panel</span>
          </NavLink>
        )}
      </nav>
      <div className="border-t border-zinc-800 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400 font-mono font-medium text-sm">
            {(user?.full_name || "U").substring(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-100 truncate">{user?.full_name}</div>
            <div className="text-xs font-mono uppercase tracking-wider text-blue-400">{user?.plan}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider border border-zinc-800 rounded-md overflow-hidden flex-1">
            <button data-testid={`${idPrefix}-lang-cs`} onClick={() => setLang("cs")} className={`flex-1 px-2 py-1 transition-colors ${lang === "cs" ? "bg-zinc-100 text-zinc-900" : "text-zinc-500 hover:text-zinc-200"}`}>CS</button>
            <button data-testid={`${idPrefix}-lang-en`} onClick={() => setLang("en")} className={`flex-1 px-2 py-1 transition-colors ${lang === "en" ? "bg-zinc-100 text-zinc-900" : "text-zinc-500 hover:text-zinc-200"}`}>EN</button>
          </div>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid={`${idPrefix}-logout`} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors">
          <SignOut weight="bold" size={16} /> {t("nav.logout")}
        </button>
      </div>
    </>
  );
};

const AppLayout = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  useEffect(() => {
    document.title = `${PAGE_TITLES[location.pathname] || "TradeMind"} — TradeMind AI`;
  }, [location.pathname]);
  React.useEffect(() => {
    if (!loading && !user) navigate("/login", { state: { from: window.location.pathname } });
    if (!loading && user && shouldShowOnboarding()) setShowGuide(true);
  }, [user, loading, navigate]);

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500">Loading...</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 lg:flex" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-zinc-950 border-r border-zinc-800 flex-col h-screen sticky top-0">
        <div className="px-6 py-5 border-b border-zinc-800"><Logo /></div>
        <NavContent idPrefix="side" />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 h-14 flex items-center justify-between px-4 bg-zinc-950/90 backdrop-blur border-b border-zinc-800" data-testid="mobile-header">
        <Logo size="sm" />
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-blue-400 border border-blue-500/30 bg-blue-500/10 rounded px-1.5 py-0.5" data-testid="mobile-plan-badge">{user?.plan}</span>
          <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" className="p-2 -mr-2 text-zinc-300 hover:text-white transition-colors">
            <List size={24} weight="bold" />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-zinc-950 border-r border-zinc-800 flex flex-col shadow-2xl">
            <div className="px-4 h-14 flex items-center justify-between border-b border-zinc-800">
              <Logo size="sm" />
              <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" className="p-2 text-zinc-400 hover:text-white transition-colors">
                <X size={20} weight="bold" />
              </button>
            </div>
            <NavContent idPrefix="mside" onNavigate={() => setMenuOpen(false)} />
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      {showGuide && <OnboardingGuide onClose={() => setShowGuide(false)} />}
    </div>
  );
};

export default AppLayout;
